import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../../services/authService';
import { notifyError } from '../../../utils/notify';

const ConfigPageRegistry = ({ onConfigureForms, onConfigureKpis }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [scope, setScope] = useState('all');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    adminAPI.getConfigPages()
      .then((res) => setRows(res?.data?.data || res?.data || []))
      .catch(() => notifyError('Unable to load page registry'));
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (scope !== 'all' && r.configurationScope !== scope) return false;
      if (!term) return true;
      return [r.moduleKey, r.pageKey, r.displayName, r.routeTabKey, r.sourcePath]
        .some((x) => String(x || '').toLowerCase().includes(term));
    });
  }, [rows, q, scope]);

  const viewInventory = async (pageKey) => {
    try {
      const res = await adminAPI.getConfigPage(pageKey);
      setDetail(res?.data?.data || res?.data);
    } catch (_e) {
      notifyError('Unable to load page inventory');
    }
  };

  return (
    <div>
      <div className="filter-bar">
        <input placeholder="Search module / page / source" value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 240 }} />
        <select value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="all">All scopes</option>
          <option value="NOT_CONFIGURABLE">NOT_CONFIGURABLE</option>
          <option value="PARTIALLY_CONFIGURABLE">PARTIALLY_CONFIGURABLE</option>
          <option value="FULLY_CONFIGURABLE">FULLY_CONFIGURABLE</option>
        </select>
      </div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Module</th><th>Page</th><th>Page Key</th><th>Route/Tab</th><th>Source</th>
              <th>Scope</th><th>Forms</th><th>Fields</th><th>Custom</th><th>KPIs</th><th>Status</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={12} style={{ textAlign: 'center', color: '#64748b', padding: 24 }}>No pages in registry</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.pageKey}>
                <td>{r.moduleKey}</td>
                <td>{r.displayName}</td>
                <td><code>{r.pageKey}</code></td>
                <td>{r.routeTabKey || '—'}</td>
                <td style={{ fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.sourcePath || '—'}</td>
                <td><span className={`ce-badge ${r.configurationScope === 'NOT_CONFIGURABLE' ? 'ce-badge--warn' : 'ce-badge--ok'}`}>{r.configurationScope}</span></td>
                <td>{r.formsCount}</td>
                <td>{r.fieldsCount}</td>
                <td>{r.customFieldsCount}</td>
                <td>{r.kpisCount}</td>
                <td><span className="ce-badge ce-badge--muted">{r.status}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => viewInventory(r.pageKey)}>View Inventory</button>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => onConfigureForms?.(r.pageKey)}>Configure Forms</button>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => onConfigureKpis?.(r.pageKey)}>Configure KPIs</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="ce-detail">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <h3 style={{ margin: 0 }}>{detail.displayName} <span className="ce-muted">({detail.pageKey})</span></h3>
            <button type="button" className="btn-secondary btn-sm" onClick={() => setDetail(null)}>Close</button>
          </div>
          <p className="ce-muted">Source: {detail.sourcePath || '—'} · Scope: {detail.configurationScope}</p>
          <div className="ce-two-col">
            <div>
              <h4>Forms</h4>
              {(detail.forms || []).map((f) => (
                <div key={f.id} className="ce-field-row">
                  <strong>{f.displayName}</strong> <span className="ce-badge">{f.formType}</span>
                  <div className="ce-muted">{f.formKey} · {(f.fields || []).length} fields</div>
                  <button type="button" className="btn-secondary btn-sm" style={{ marginTop: 6 }} onClick={() => onConfigureForms?.(detail.pageKey, f.id)}>Open in Form Builder</button>
                </div>
              ))}
              {(detail.forms || []).length === 0 && <p className="ce-muted">No forms registered.</p>}
            </div>
            <div>
              <h4>KPIs</h4>
              {(detail.kpiDefinitions || []).map((k) => (
                <div key={k.id} className="ce-field-row">
                  <strong>{k.name}</strong> <span className="ce-badge">{k.kpiClass}</span>
                  <div className="ce-muted">{k.kpiKey} · {k.aggregation || '—'}</div>
                </div>
              ))}
              {(detail.kpiDefinitions || []).length === 0 && <p className="ce-muted">No KPIs on this page.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigPageRegistry;
